import React, { useContext } from 'react'
import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle as faCheckCircleLight, faExclamationCircle, faEyeSlash, faLock,
} from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Checkbox, ListItem, Tag, Text,
} from '@domain-group/suit-theme'

import useCampaignQuoteItem from '@hooks/useCampaignQuoteItem'
import useProvider from '@hooks/useProvider'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'

import { QuoteBuilderContext } from '@components/QuoteBuilder/components/QuoteBuilderContextProvider'
import CampaignQuoteItemToolTip from '@components/CampaignQuoteItemToolTip'
import HintWrapper from '@components/HintWrapper'
import OrderItemOrQuoteItemManagement from '@components/OrderItemOrQuoteItemManagement'

import type { CampaignQuoteItemModel, CampaignQuoteModel } from '@models/types'

import { quoteItemSettingsHiddenText } from '@functions/campaignQuoteItem'

interface CampaignQuoteItemProps {
  campaignQuoteItem: CampaignQuoteItemModel,
  dragHandleProps: object,
  dragSnapshot: {
    isDragging: boolean,
  },
  draggableProps: object,
  hideDates: boolean,
  hidePrices: boolean,
}

type InvalidItems = CampaignQuoteModel['invalidItems']

const CampaignQuoteItem = React.forwardRef((props: CampaignQuoteItemProps, ref) => {
  const {
    campaignQuoteItem,
    dragHandleProps,
    dragSnapshot,
    draggableProps,
    hideDates,
    hidePrices,
  } = props

  const quoteBuilderContext = useContext(QuoteBuilderContext)
  const {
    campaignQuotePayload,
  } = quoteBuilderContext

  const { isDragging } = dragSnapshot || {}

  const campaignQuoteItemPayload = useCampaignQuoteItem(campaignQuoteItem)
  const {
    callbacks: {
      createOrEditCampaignQuoteItem,
      toggleCampaignQuoteItemSelection,
    },
    campaignQuoteItem: {
      compulsory,
      hidden,
      id,
      internal_notes,
      locked,
      paid_by,
      price_override,
      provider_id,
      selected,
    },
    hasTags,
    publicationEditionValid,
    requiredByDateString,
    tags,
    titleString,
  } = campaignQuoteItemPayload

  const invalidItems = (campaignQuotePayload?.campaignQuote.invalidItems ?? []) as InvalidItems
  const invalidPrice = invalidItems?.find(item => item?.campaign_quote_item_id === campaignQuoteItem.id)

  const { redirectUrl } = useRedirectUrlWithCacheKey([campaignQuoteItem.campaign_quote_id!])

  const { provider: { title: providerTitle } } = useProvider({ id: provider_id })

  return (
    <ListItem
      boxProps={{
        alignItems: 'center',
        backgroundColor: 'white',
        borderBottom: '1px dashed',
        borderColor: 'lineColor',
        paddingY: 'medium',
      }}
      forwardProps={{
        ...draggableProps,
        ...dragHandleProps,
      }}
      isDragging={isDragging}
      key={id}
      onClick={() => createOrEditCampaignQuoteItem()}
      // @ts-expect-error TS2322
      ref={ref}
      showAngleIcon
    >
      <Checkbox
        checked={selected}
        iconChecked={locked ? faLock : faCheckCircle}
        iconDefault={locked ? faLock : faCheckCircleLight}
        color={selected && !locked ? 'green' : 'lightGrey'}
        // @ts-expect-error TS7006
        onClick={(e) => {
          if (!compulsory && !locked){
            toggleCampaignQuoteItemSelection(e, {
              include_campaign_quote_item_quote_dynamic_items: true,
              include_campaign_quote_item_campaign_quote: true,
            })
          }
        }}
      />

      <Box flexDirection="column" alignItems="center" justifyContent="center">
        <Box alignItems="center">
          <Box flexDirection="column" alignItems="center" flex={4}>
            <Box alignItems="center" position="relative">
              <HintWrapper
                hint={providerTitle || 'Unknown Provider'}
                renderChildrenOnly={!providerTitle}
              >
                <Text
                  color={hidden ? 'faintGrey' : 'bodyFontColor'}
                  fontSize="small"
                  lineHeight="1.3"
                  variant="twoLineEllipsis"
                >
                  {titleString}
                </Text>
              </HintWrapper>

              {requiredByDateString && (
                <Text
                  color="faintGrey"
                  display={['none', 'block']}
                  fontSize="xsmall"
                  lineHeight="1.3"
                  marginLeft="medium"
                >
                  {requiredByDateString}
                </Text>
              )}
            </Box>

            {!publicationEditionValid && (
              <Text color="orange" fontSize="xsmall" marginTop="medium">
                <FontAwesomeIcon icon={faExclamationCircle} /> Edition has Passed
              </Text>
            )}

            {internal_notes && (
              <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
                {internal_notes}
              </Text>
            )}

            {hasTags && (
              <Box alignItems="center" marginTop="medium">
                {/*
                 // @ts-expect-error TS7006 */}
                {tags.map(tag => (
                  <Tag key={tag}>{tag}</Tag>
                ))}

                {paid_by !== 'vendor' && <Tag>Paid by {paid_by === 'agent' ? 'Agent' : 'Agency'}</Tag>}
              </Box>
            )}
          </Box>

          {!hidden && (
            <Box flex={1} justifyContent="space-between" alignItems="center">
              <Text
                color={invalidPrice ? 'orange' : 'bodyFontLightColor'}
                fontSize="small"
                lineHeight="1.3"
                marginLeft="auto"
              >
                <HintWrapper
                  // @ts-expect-error TS2322
                  hint={quoteItemSettingsHiddenText({ hideDates, hidePrices })}
                >
                  {(hidePrices || hideDates)
                    && <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '12px', marginRight: '4px' }} />}
                </HintWrapper>
                {formatMoney(price_override!)}
              </Text>

              {!!invalidPrice && (
                <CampaignQuoteItemToolTip invalidPrice={invalidPrice} />
              )}
            </Box>
          )}
        </Box>

        <OrderItemOrQuoteItemManagement
          enabledFeatureKeys={['artwork.edit']}
          itemPayload={campaignQuoteItemPayload}
          redirectUrl={redirectUrl}
        />
      </Box>
    </ListItem>
  )
})

export default CampaignQuoteItem
